/**
 * CollectionsReport (COLLECT-01) — "who owes us", the collections call
 * worklist off the receivables mirror (MIR-12). One row per customer with a
 * net open balance: aging buckets (same day-math as the 360 header's Balance
 * strip), oldest item, last payment seen, phone — sorted Over-90 first so
 * the person making collection calls starts at the top.
 *
 * Competitor framing: ServiceTitan's AR screen + QuickBooks "A/R Aging
 * Detail" both shape this as customer rows × bucket columns with invoice
 * drill-down — adopted. Adapted: rows click through to the 360 / invoice
 * pages, and decade-old builder invoices read "8.1 yr" not "2944 days".
 * Payment promises / collection notes (ESC's Collectn table) are a later
 * slice on this same surface.
 *
 * Aggregation is SERVER-SIDE (collections_worklist RPC) — 71k receivable
 * rows are a SQL job, not a pagedAll job.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, ChevronDown, ChevronRight, Mail, StickyNote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { displayCustomerName } from "@/lib/format";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { ListSearch, CsvButton, downloadCsv } from "./listCore";
import { EmailTemplateModal } from "@/features/comms/EmailTemplateModal";

interface WorklistRow {
  custno: string;
  fullname: string | null;
  phone1: string | null;
  city: string | null;
  total_owed: number;
  bucket_current: number;
  bucket_31_60: number;
  bucket_61_90: number;
  bucket_over_90: number;
  open_invoices: number;
  oldest_days: number;
  last_paid: string | null;
}

interface OpenInvoice {
  invoice: string;
  inv_date: string | null;
  inv_amt: number | null;
  paid: number | null;
  dept: string | null;
  job_number: string | null;
}

function useWorklist() {
  return useQuery({
    queryKey: ["collections_worklist"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<WorklistRow[]> => {
      const { data, error } = await supabase.rpc("collections_worklist");
      if (error) throw error;
      return (data ?? []) as WorklistRow[];
    },
  });
}

const money = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : v.toLocaleString("en-US", { style: "currency", currency: "USD" });

/** ESC dates live in the mirror as UTC-midnight timestamps — format in UTC
 *  or the browser's timezone shifts them a day (Feb 18 read as Feb 17). The
 *  statement renderer (edge fn, UTC) already shows the right day; this keeps
 *  the drill-down consistent with it. */
function utcDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** 47 → "47d", 200 → "6.6 mo", 2944 → "8.1 yr" — zombie AR reads honestly. */
function ageLabel(days: number): string {
  if (days <= 90) return `${days}d`;
  if (days < 365) return `${(days / 30.4).toFixed(1)} mo`;
  return `${(days / 365).toFixed(1)} yr`;
}

export function CollectionsReport() {
  const { data, isLoading } = useWorklist();
  const all = data ?? [];
  const [search, setSearch] = useState("");
  const [only90, setOnly90] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  // TMPL-01: "Email Statement" — preview + send the rendered statement.
  const [statementFor, setStatementFor] = useState<WorklistRow | null>(null);
  const navigate = useNavigate();

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(
      (r) =>
        (!only90 || r.bucket_over_90 > 0) &&
        (!q ||
          (r.fullname ?? "").toLowerCase().includes(q) ||
          r.custno.includes(q) ||
          (r.city ?? "").toLowerCase().includes(q)),
    );
  }, [all, search, only90]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (a, r) => ({
          owed: a.owed + r.total_owed,
          over90: a.over90 + r.bucket_over_90,
        }),
        { owed: 0, over90: 0 },
      ),
    [rows],
  );

  function exportCsv() {
    downloadCsv(
      `collections-worklist-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Customer", "Custno", "City", "Phone", "Current", "31-60", "61-90", "Over 90", "Total owed", "Open invoices", "Oldest", "Last paid"],
      rows.map((r) => [
        displayCustomerName(null, r.fullname) || r.custno,
        r.custno,
        r.city ?? "",
        r.phone1 ?? "",
        r.bucket_current,
        r.bucket_31_60,
        r.bucket_61_90,
        r.bucket_over_90,
        r.total_owed,
        r.open_invoices,
        ageLabel(r.oldest_days),
        r.last_paid ? r.last_paid.slice(0, 10) : "",
      ]),
    );
  }

  const toggle = (custno: string) => {
    const next = new Set(open);
    if (next.has(custno)) next.delete(custno);
    else next.add(custno);
    setOpen(next);
  };

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ListSearch value={search} onChange={setSearch} placeholder="Search name, customer #, city…" />
        <label className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 text-xs font-medium text-slate-600">
          <input
            type="checkbox"
            checked={only90}
            onChange={(e) => setOnly90(e.target.checked)}
            className="h-3.5 w-3.5 accent-brand-700"
          />
          Only 90+ overdue
        </label>
        <CsvButton onClick={exportCsv} />
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500">
          {all.length === 0
            ? "Nobody owes anything — every invoice is paid off."
            : "No customers match this filter."}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-red-50/60 via-transparent to-transparent px-4 py-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-50 text-red-600">
              <Banknote className="h-3.5 w-3.5" />
            </span>
            <span className="text-[14.5px] font-bold tracking-tight text-slate-900">
              Collection Manager — who owes
            </span>
            <InfoTip label="Where these balances come from">
              Open items from ESC&apos;s receivables (invoice amount minus paid, where not paid off), refreshed nightly. The aging
              buckets match the Balance strip on the customer&apos;s page.
            </InfoTip>
            <span className="ml-auto text-xs tabular-nums text-slate-500">
              {rows.length} customers · {money(totals.owed)} open ·{" "}
              <span className={cn(totals.over90 > 0 && "font-semibold text-red-600")}>
                {money(totals.over90)} over 90
              </span>
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[12px] font-semibold text-slate-500">
                <th className="px-4 py-2">Customer</th>
                <th className="px-2 py-2">Phone</th>
                <th className="px-2 py-2 text-right">Current</th>
                <th className="px-2 py-2 text-right">31–60</th>
                <th className="px-2 py-2 text-right">61–90</th>
                <th className="px-2 py-2 text-right">Over 90</th>
                <th className="px-2 py-2 text-right">Total owed</th>
                <th className="px-2 py-2 text-right">Oldest</th>
                <th className="px-4 py-2 text-right">Last paid</th>
                <th className="w-10 px-2 py-2" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <CustomerRows
                  key={r.custno}
                  r={r}
                  open={open.has(r.custno)}
                  onToggle={() => toggle(r.custno)}
                  onOpenCustomer={() => navigate(`/customers/${r.custno}`)}
                  onOpenInvoice={(inv) => navigate(`/invoice/${inv}?from=reports`)}
                  onEmailStatement={() => setStatementFor(r)}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {statementFor && (
        <EmailTemplateModal
          custno={statementFor.custno}
          customerName={
            displayCustomerName(null, statementFor.fullname) || statementFor.custno
          }
          templateKey="statement"
          title={`Email Statement — ${displayCustomerName(null, statementFor.fullname) || statementFor.custno}`}
          messageType="statement"
          onClose={() => setStatementFor(null)}
        />
      )}
    </div>
  );
}

function CustomerRows({
  r,
  open,
  onToggle,
  onOpenCustomer,
  onOpenInvoice,
  onEmailStatement,
}: {
  r: WorklistRow;
  open: boolean;
  onToggle: () => void;
  onOpenCustomer: () => void;
  onOpenInvoice: (invoice: string) => void;
  onEmailStatement: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-b border-slate-50 transition-colors hover:bg-brand-50/40"
      >
        <td className="px-4 py-2">
          <span className="inline-flex items-center gap-1.5">
            {open ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenCustomer();
              }}
              title="Pull up this customer's record"
              className="font-medium text-brand-700 hover:underline"
            >
              {displayCustomerName(null, r.fullname) || r.custno}
            </button>
            {r.city && <span className="text-xs text-slate-400">{r.city}</span>}
          </span>
        </td>
        <td className="px-2 py-2 text-xs">
          {r.phone1 ? (
            <a
              href={`tel:${r.phone1.replace(/[^0-9+]/g, "")}`}
              onClick={(e) => e.stopPropagation()}
              className="text-slate-600 hover:text-brand-700"
            >
              {r.phone1}
            </a>
          ) : (
            <span className="text-slate-300">—</span>
          )}
        </td>
        <Bucket v={r.bucket_current} />
        <Bucket v={r.bucket_31_60} />
        <Bucket v={r.bucket_61_90} />
        <td
          className={cn(
            "px-2 py-2 text-right tabular-nums",
            r.bucket_over_90 > 0 ? "font-semibold text-red-600" : "text-slate-300",
          )}
        >
          {r.bucket_over_90 !== 0 ? money(r.bucket_over_90) : "—"}
        </td>
        <td className="px-2 py-2 text-right font-semibold tabular-nums text-slate-900">
          {money(r.total_owed)}
        </td>
        <td
          className={cn(
            "px-2 py-2 text-right text-xs tabular-nums",
            r.oldest_days > 365 ? "font-medium text-amber-700" : "text-slate-500",
          )}
        >
          {ageLabel(r.oldest_days)}
        </td>
        <td className="px-4 py-2 text-right text-xs tabular-nums text-slate-500">
          {r.last_paid ? utcDate(r.last_paid) : "never"}
        </td>
        <td className="px-2 py-2 text-right">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEmailStatement();
            }}
            title="Email Statement — preview first, then send"
            className="rounded-md border border-hairline bg-white p-1.5 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-700"
          >
            <Mail className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {open && <OpenInvoiceRows custno={r.custno} onOpenInvoice={onOpenInvoice} />}
      {open && (
        <CollectionNotesRow
          custno={r.custno}
          customerName={displayCustomerName(null, r.fullname) || r.custno}
        />
      )}
    </>
  );
}

function Bucket({ v }: { v: number }) {
  return (
    <td
      className={cn(
        "px-2 py-2 text-right tabular-nums",
        v !== 0 ? "text-slate-700" : "text-slate-300",
      )}
    >
      {v !== 0 ? money(v) : "—"}
    </td>
  );
}

/**
 * COLLECT-02 #2 — collection notes & promises, inside the drill-down where
 * the collection call actually happens. ESC's Collectn history (read-only,
 * loads after Seth runs `-Section collectn`) merges chronologically with
 * Pegasus-native notes; an optional PROMISE DATE becomes a real follow-up
 * (source 'collections') so the promise resurfaces on Today — never a note
 * someone has to remember to re-read.
 */
interface EscNote {
  key: string;
  note_date: string | null;
  note: string | null;
  action: string | null;
  follow_up: string | null;
}
interface PegNote {
  id: string;
  note: string;
  promise_date: string | null;
  created_by: string | null;
  created_at: string;
}

function CollectionNotesRow({
  custno,
  customerName,
}: {
  custno: string;
  customerName: string;
}) {
  const qc = useQueryClient();
  const canEdit = useCan("collections.edit");

  const escNotes = useQuery({
    queryKey: ["collectn_esc", custno],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EscNote[]> => {
      const { data, error } = await supabase
        .from("mirror_collection_note")
        .select("key, note_date, note, action, follow_up")
        .eq("custno", custno)
        .order("note_date", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as EscNote[];
    },
  });
  const pegNotes = useQuery({
    queryKey: ["collectn_peg", custno],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PegNote[]> => {
      const { data, error } = await supabase
        .from("pegasus_collection_note")
        .select("id, note, promise_date, created_by, created_at")
        .eq("custno", custno)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as PegNote[];
    },
  });

  const [text, setText] = useState("");
  const [promise, setPromise] = useState("");

  const add = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      const by = u?.user?.email ?? null;
      let followupId: string | null = null;
      if (promise) {
        // The promise IS a follow-up — it resurfaces on Today when due.
        const { data: fu, error: fuErr } = await supabase
          .from("pegasus_followup")
          .insert({
            custno,
            title: `Collections — ${customerName} promised payment`,
            details: text.trim(),
            due_date: promise,
            source: "collections",
            created_by: by,
          })
          .select("id")
          .single();
        if (fuErr) throw fuErr;
        followupId = (fu as { id: string }).id;
      }
      const { error } = await supabase.from("pegasus_collection_note").insert({
        custno,
        note: text.trim(),
        promise_date: promise || null,
        followup_id: followupId,
        created_by: by,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setText("");
      setPromise("");
      qc.invalidateQueries({ queryKey: ["collectn_peg", custno] });
      qc.invalidateQueries({ queryKey: ["followups"] });
    },
  });

  const merged = [
    ...(pegNotes.data ?? []).map((n) => ({
      id: n.id,
      when: n.created_at,
      text: n.note,
      tag: n.promise_date ? `promised by ${utcDate(n.promise_date)}` : null,
      by: n.created_by ? n.created_by.split("@")[0] : null,
      esc: false,
    })),
    ...(escNotes.data ?? []).map((n) => ({
      id: n.key,
      when: n.note_date ?? "",
      text: [n.note, n.action ? `(${n.action})` : null].filter(Boolean).join(" "),
      tag: n.follow_up ? `follow-up ${utcDate(n.follow_up)}` : null,
      by: null,
      esc: true,
    })),
  ].sort((a, b) => (b.when || "").localeCompare(a.when || ""));

  return (
    <tr className="border-b border-slate-100 bg-amber-50/30">
      <td colSpan={10} className="px-12 py-3">
        <div className="flex items-start gap-6">
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-slate-500">
              <StickyNote className="h-3 w-3" />
              Notes &amp; Promises
              <InfoTip label="About these notes">
                Notes added here and ESC&apos;s collection history (read-only), newest first. A promised-by date also puts a follow-up on Today when it&apos;s due.
              </InfoTip>
            </div>
            {merged.length === 0 ? (
              <p className="text-xs italic text-slate-400">No collection notes yet.</p>
            ) : (
              <ul className="max-h-44 space-y-1 overflow-auto pr-2">
                {merged.map((n) => (
                  <li key={n.id} className="text-xs leading-snug text-slate-700">
                    <span className="text-slate-400">
                      {n.when ? utcDate(n.when) : "—"}
                    </span>{" "}
                    {n.text}
                    {n.tag && (
                      <span className="ml-1.5 rounded bg-brand-50 px-1 py-0.5 text-[10px] font-semibold text-brand-700">
                        {n.tag}
                      </span>
                    )}
                    {n.by && <span className="ml-1.5 text-slate-400">— {n.by}</span>}
                    {n.esc && (
                      <span className="ml-1.5 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold text-slate-400">
                        esc
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {canEdit && (
            <div className="w-72 shrink-0">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                placeholder={`Spoke with ${customerName} — said the check goes out Friday…`}
                className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-xs"
              />
              <div className="mt-1.5 flex items-center gap-2">
                <label className="flex items-center gap-1 text-[11px] text-slate-500">
                  Promised by
                  <input
                    type="date"
                    value={promise}
                    onChange={(e) => setPromise(e.target.value)}
                    className="rounded-md border border-hairline bg-white px-1.5 py-1 text-[11px]"
                  />
                </label>
                <button
                  type="button"
                  disabled={!text.trim() || add.isPending}
                  onClick={() => add.mutate()}
                  title={
                    promise
                      ? "Saves the note AND puts the promise on Today when it's due"
                      : "Save the note"
                  }
                  className="ml-auto rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300"
                >
                  {add.isPending ? "Saving…" : promise ? "Save + Track Promise" : "Save Note"}
                </button>
              </div>
              {add.isError && (
                <p className="mt-1 text-[11px] font-medium text-red-600">
                  {(add.error as Error)?.message ?? "save failed"}
                </p>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

/** The drill-down: this customer's open items, fetched on expand. */
function OpenInvoiceRows({
  custno,
  onOpenInvoice,
}: {
  custno: string;
  onOpenInvoice: (invoice: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["collections_detail", custno],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<OpenInvoice[]> => {
      const { data, error } = await supabase
        .from("mirror_receivable")
        .select("invoice, inv_date, inv_amt, paid, dept, job_number")
        .eq("custno", custno)
        .is("paid_off", null)
        .order("inv_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as OpenInvoice[];
    },
  });

  if (isLoading) {
    return (
      <tr className="border-b border-slate-50 bg-slate-50/50">
        <td colSpan={10} className="px-12 py-2 text-xs text-slate-400">
          Loading open invoices…
        </td>
      </tr>
    );
  }

  return (
    <>
      {(data ?? []).map((inv) => {
        const bal = (inv.inv_amt ?? 0) - (inv.paid ?? 0);
        return (
          <tr
            key={`${custno}-${inv.invoice}-${inv.inv_date ?? ""}-${bal}`}
            onClick={() => onOpenInvoice(inv.invoice)}
            title="Open the invoice"
            className="cursor-pointer border-b border-slate-50 bg-slate-50/50 text-xs transition-colors hover:bg-brand-50/40"
          >
            <td colSpan={2} className="py-1.5 pl-12 pr-4 text-slate-600">
              <span className="font-mono">{inv.invoice}</span>
              {inv.inv_date && (
                <span className="ml-2 text-slate-400">{utcDate(inv.inv_date)}</span>
              )}
              {inv.job_number && (
                <span className="ml-2 rounded bg-slate-100 px-1 text-[10px] text-slate-500">
                  job {inv.job_number}
                </span>
              )}
              {bal < 0 && (
                <span className="ml-2 rounded bg-emerald-50 px-1 text-[10px] font-semibold text-emerald-700">
                  credit
                </span>
              )}
            </td>
            <td colSpan={4} className="px-2 py-1.5 text-right tabular-nums text-slate-400">
              {money(inv.inv_amt)} billed · {money(inv.paid ?? 0)} paid
            </td>
            <td className="px-2 py-1.5 text-right font-medium tabular-nums">
              {money(bal)}
            </td>
            <td colSpan={3} />
          </tr>
        );
      })}
    </>
  );
}
